import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GoogleBusinessCard from '../components/GoogleBusinessCard';

const { FiMapPin, FiMessageCircle, FiClock } = FiIcons;

function Contact() {
  useEffect(() => {
    // Add chat widget script to head - only on Contact page
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.charset = 'utf-8';
    script.innerHTML = `
      window.addEventListener('load', () => { 
        const widgetElement = document.createElement('charla-widget'); 
        widgetElement.setAttribute("p", "cd04619e-e660-48c3-b7d5-46791a96c803"); 
        document.body.appendChild(widgetElement); 
        const widgetCode = document.createElement('script'); 
        widgetCode.src = 'https://app.getcharla.com/widget/widget.js'; 
        document.body.appendChild(widgetCode); 
      })
    `;
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      document.head.removeChild(script);
      const widgetElements = document.querySelectorAll('charla-widget');
      widgetElements.forEach(element => element.remove());
      const widgetScripts = document.querySelectorAll('script[src="https://app.getcharla.com/widget/widget.js"]');
      widgetScripts.forEach(script => script.remove());
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1558655146-9f40138edfeb?q=80&w=2064&auto=format&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-dark-bg"></div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Contact BoroughTV
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Connect with NYC's local TV station
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <GoogleBusinessCard />
          </motion.div>

          {/* Chat Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-dark-card p-8 rounded-lg border border-dark-border">
              <h2 className="text-2xl font-bold mb-6 gradient-text">Chat With Us</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-accent-blue/20 p-3 rounded-full">
                    <SafeIcon icon={FiMessageCircle} className="text-accent-blue text-xl" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-2">Live Chat Support</h3>
                    <p className="text-gray-400">
                      Get instant answers to your questions about BoroughTV programming, 
                      community showcases, and collaboration opportunities.
                    </p>
                  </div>
                </div>

                <div className="bg-primary-blue/20 border border-primary-blue/30 rounded-lg p-4">
                  <h4 className="font-semibold text-accent-blue mb-2">How to Get Started:</h4>
                  <ol className="text-gray-300 text-sm space-y-1">
                    <li>1. Click the chat widget in the bottom right corner</li>
                    <li>2. Tell us about your project or question</li>
                    <li>3. Get connected with our team instantly</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-dark-card p-8 rounded-lg border border-dark-border">
              <h2 className="text-2xl font-bold mb-6 gradient-text">Office Hours</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-accent-blue/20 p-3 rounded-full">
                    <SafeIcon icon={FiClock} className="text-accent-blue text-xl" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">Business Hours</h3>
                    <p className="text-gray-400">Monday - Friday</p>
                    <p className="text-gray-400">9:00 AM - 5:00 PM ET</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Contact;